import {
    Create,
    Datagrid,
    DateField,
    DeleteButton,
    Edit,
    EditButton,
    email,
    EmailField,
    List,
    minLength,
    required,
    maxLength,
    SaveButton,
    SimpleForm,
    TextField,
    TextInput,
    BooleanInput,
    AutocompleteArrayInput,
    useLocale,
    useRecordContext,
    useTranslate,
    TabbedForm,
} from 'react-admin';
import { ReferenceArrayInput, EditToolbar } from '@components';
import { Typography, Box, Divider } from '@mui/material';

interface User {
    id: number;
    name: string;
    email: string;
    userGroupsIds: number[];
    roleIds: number[];
    // Email signature fields
    title?: string | null;
    phone?: string | null;
    emailSignature?: string | null;
    isAway: boolean;
    awayMessage?: string | null;
}

const requiredValidation = [required()];
const validateEmail = [required(), email()];
const validatePassword = [required(), minLength(8)];
const validateOptionalPassword = [minLength(8)];

export const UserList = () => {
    const locale = useLocale();

    const userFilter = [
        <TextInput sx={{ marginBottom: 1 }} source="email" defaultValue="" key="email_q" alwaysOn />,    
        <TextInput sx={{ marginBottom: 1 }} source="name" defaultValue="" key="name_q" />,
    ];

    return (
    <List
    filters={userFilter}
    >
        <Datagrid bulkActionButtons={false} rowClick={false} sort={{ field: 'id', order: 'DESC' }}>
            <TextField source="id" />
            <TextField source="name" />
            <EmailField source="email" />
            <DateField source="createdAt" showTime locales={locale} />
            <DateField source="updatedAt" showTime locales={locale} />
            <EditButton />
        </Datagrid>
    </List>
);
}

export const UserCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="name" validate={requiredValidation} fullWidth autoComplete='off' />
            <TextInput source="email" validate={validateEmail} fullWidth autoComplete='off' />
            <TextInput
                source="password"
                type="password"
                validate={validatePassword}
                fullWidth
                autoComplete="new-password"
                inputProps={{ autoComplete: 'new-password' }}
            />
            <ReferenceArrayInput source="userGroupsIds" reference="group">
                <AutocompleteArrayInput
                    optionText="name"
                    openOnFocus={false}
                    TextFieldProps={{ autoComplete: 'off' }}
                />
            </ReferenceArrayInput>
            <ReferenceArrayInput source="roleIds" reference="role">
                <AutocompleteArrayInput
                    optionText="name"
                    openOnFocus={false}
                    TextFieldProps={{ autoComplete: 'off' }}
                />
            </ReferenceArrayInput>
        </SimpleForm>
    </Create>
);



export const UserEdit = () => {
    const translate = useTranslate();
    const record = useRecordContext<User>();
    const defaultGroupIds = record?.userGroupsIds || [];
    const defaultRoleIds = record?.roleIds || [];
    return (
    <Edit mutationMode="pessimistic">
        <TabbedForm toolbar={<EditToolbar showDeleteButton={true} />}>
            {/* General Tab */}
            <TabbedForm.Tab label="resources.user.tabs.general">
                <TextInput source="name" validate={requiredValidation} fullWidth autoComplete='off' />
                <TextInput source="email" validate={validateEmail} autoComplete='off' fullWidth />
                <TextInput
                    source="password"
                    type="password"
                    validate={validateOptionalPassword}
                    fullWidth
                    autoComplete="new-password"
                    inputProps={{ autoComplete: 'new-password' }}
                />
                <ReferenceArrayInput source="userGroupsIds" reference="group" defaultValue={defaultGroupIds}>
                    <AutocompleteArrayInput
                        optionText="name"
                        openOnFocus={false}
                        TextFieldProps={{ autoComplete: 'off' }}
                    />
                </ReferenceArrayInput>
                <ReferenceArrayInput source="roleIds" reference="role" defaultValue={defaultRoleIds}>
                    <AutocompleteArrayInput
                        optionText="name"
                        openOnFocus={false}
                        TextFieldProps={{ autoComplete: 'off' }}
                    />
                </ReferenceArrayInput>
            </TabbedForm.Tab>

            {/* Email Signature Tab */}
            {/* <TabbedForm.Tab label="resources.user.tabs.emailSignature">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.user.emailSignature.description')}
                </Typography>

                <TextInput
                    source="title"
                    label="resources.user.fields.title"
                    fullWidth
                    helperText={translate('resources.user.emailSignature.titleHelper')}
                    validate={[maxLength(100)]}
                />
                <TextInput
                    source="phone"
                    label="resources.user.fields.phone"
                    fullWidth
                    helperText={translate('resources.user.emailSignature.phoneHelper')}
                    validate={[maxLength(50)]}
                />

                <Divider sx={{ my: 2 }} />

                <TextInput
                    source="emailSignature"
                    label="resources.user.fields.emailSignature"
                    multiline
                    minRows={4}
                    maxRows={10}
                    fullWidth
                    helperText={translate('resources.user.emailSignature.signatureHelper')}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {translate('resources.user.emailSignature.awayStatus')}
                </Typography>
                <BooleanInput
                    source="isAway"
                    label="resources.user.fields.isAway"
                />
                <TextInput
                    source="awayMessage"
                    label="resources.user.fields.awayMessage"
                    multiline
                    minRows={2}
                    maxRows={5}
                    fullWidth
                    helperText={translate('resources.user.emailSignature.awayMessageHelper')}
                />
            </TabbedForm.Tab> */}
        </TabbedForm>
    </Edit>
    );
};
